import { Controller } from '@hotwired/stimulus';

// Goals v3 filter toggles without the full page load. A status or
// discipline toggle click fetches its own href over XHR — the server runs
// the same action with the new selection (so the counts, the section rules,
// the voided semantics, and the per-user filter memory all stay
// server-truth) and returns just the roster partial, which swaps in place.
// The URL is pushed so the page stays bookmarkable and back/forward re-fetch
// the state they point at; rows that were open before the swap are reopened
// after it, and an active "Toggle all" master (in the card header, outside
// the swap) is applied again to the new rows, so the master and the rows
// agree. Any fetch problem falls back to a real navigation, which is
// exactly the old behavior.
export default class extends Controller {
  connect() {
    this._onClick = (event) => this.toggleClicked(event);
    this.element.addEventListener('click', this._onClick);
    this._onPopstate = (event) => {
      if (event.state && event.state.goalsV3Filters) this._load(window.location.href, { push: false });
    };
    window.addEventListener('popstate', this._onPopstate);
    // Seed the current entry so the first Back after a swap lands on state
    // this controller recognizes and re-fetches.
    if (!window.history.state || !window.history.state.goalsV3Filters) {
      window.history.replaceState({ ...window.history.state, goalsV3Filters: true }, '', window.location.href);
    }
  }

  disconnect() {
    this.element.removeEventListener('click', this._onClick);
    window.removeEventListener('popstate', this._onPopstate);
  }

  toggleClicked(event) {
    const link = event.target.closest('a.gv3-filter-toggle');
    if (!link || !this.element.contains(link)) return;
    event.preventDefault();
    this._load(link.href, { push: true });
  }

  async _load(url, { push }) {
    if (this._busy) return;
    this._busy = true;
    const openIds = this._openGoalIds();
    this._showLoading();
    try {
      const response = await fetch(url, {
        headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'text/html' },
        credentials: 'same-origin',
      });
      // A redirect (an expired session's sign-in page, the flag-off bounce, a
      // deleted patient) arrives ok with a whole foreign page; that is a real
      // navigation, never something to write inside the roster.
      if (!response.ok || response.redirected) {
        window.location.assign(url);
        return;
      }
      this.element.innerHTML = await response.text();
      if (push) window.history.pushState({ goalsV3Filters: true }, '', url);
      this._reopenRows(openIds);
      // Stimulus connects the swapped-in controllers from a mutation
      // observer; a turn later the new rows answer to their goals-v3-row
      // controllers, which the masters drive.
      setTimeout(() => this._reapplyMasters(), 0);
    } catch {
      window.location.assign(url);
    } finally {
      this._busy = false;
      this._hideLoading();
    }
  }

  // The mask carries a message: the roster dims and a pill up top says the
  // swap is in flight. The pill is a real element appended for the load —
  // the innerHTML swap discards it with the old roster, and _hideLoading
  // covers the failure paths where the old content stays.
  _showLoading() {
    this.element.setAttribute('aria-busy', 'true');
    this.element.classList.add('gv3-filters-loading');
    this._loadingPill = document.createElement('div');
    this._loadingPill.className = 'gv3-filters-loading-pill';
    this._loadingPill.setAttribute('role', 'status');
    this._loadingPill.innerHTML =
      '<span class="spinner-border spinner-border-sm mr-2" aria-hidden="true"></span>Loading goals…';
    this.element.appendChild(this._loadingPill);
  }

  _hideLoading() {
    this.element.removeAttribute('aria-busy');
    this.element.classList.remove('gv3-filters-loading');
    if (this._loadingPill) {
      this._loadingPill.remove();
      this._loadingPill = null;
    }
  }

  // The disclosure buttons carry aria-controls="goal-detail-<id>"; the open
  // ones name the rows to restore after the swap.
  _openGoalIds() {
    return Array.from(this.element.querySelectorAll('[aria-controls^="goal-detail-"][aria-expanded="true"]')).map(
      (el) => el.getAttribute('aria-controls').replace('goal-detail-', '')
    );
  }

  // An active master promised every row open with its panel, and the rows
  // came back with every panel closed while the master kept its state (it
  // lives outside the swap), so the next click on it would close everything.
  // It applies again to the new rows.
  _reapplyMasters() {
    document.querySelectorAll('[data-controller~="goals-v3-master-toggle"]').forEach((element) => {
      const master = this.application.getControllerForElementAndIdentifier(element, 'goals-v3-master-toggle');
      if (master && master.activeValue) master.reapply();
    });
  }

  // Mirrors goals-v3-row's applyOpen with plain DOM operations — the row
  // controllers may not have reconnected yet when the new HTML lands, and
  // the plugin reads `show` from the DOM at call time, so later per-row
  // toggles stay consistent with this.
  _reopenRows(ids) {
    ids.forEach((id) => {
      const detail = this.element.querySelector(`#goal-detail-${id}`);
      if (!detail) return;
      detail.classList.remove('collapsing');
      detail.classList.add('collapse', 'show');
      detail.style.height = '';
      const disclosure = this.element.querySelector(`[aria-controls="goal-detail-${id}"]`);
      if (disclosure) {
        disclosure.setAttribute('aria-expanded', 'true');
        const row = disclosure.closest('.gv3-expand-row');
        if (row) row.classList.add('is-open');
      }
    });
  }
}
